import * as React from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IKeytipProps, ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  FocusZone,
  FocusZoneDirection,
  FocusZoneTabbableElements,
  getRTL,
  KeyCodes,
  memoizeFunction,
  mergeStyles,
  VerticalDivider,
} from "@fluentui/react";
import {
  useCommonArrowNavGroup,
  DivWithTabsterAttributes,
} from "@1js/acui-focus";

import type { MenuOpenOnExecuteParameter } from "@1js/acui-common";
import { doNotAllowFocusEventToPropagate } from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
import {
  isResponsiveRibbonScalingFeatureEnabled,
  isShyRibbonEnabled,
  ribbonExperiments,
  platformFeatures,
  isChatPPTEnabled,
} from "@1js/acui-experiments";
import type { MenuDefinition, MenuSection } from "@1js/acui-menu";
import {
  AppAutoOverflowPaddleControl,
  AppOverflowPaddleControl,
} from "@1js/acui-overflow-paddle-control";
import {
  AppResizeGroup,
  MeasuredContext,
  OverflowNotifier,
} from "@1js/acui-resize-group";
import { AppThemeConsumer } from "@1js/acui-theme";
import { getRectSpanningOffset, isReducedAnimations } from "@1js/window-util";

import type {
  InitialTabRenderRecords,
  RibbonScalingStateWithCacheKeys,
} from "..";
import {
  chevronWidth,
  tabContentContainerPadding,
  getGroupDividerScalingStepClassName,
} from "..";
import {
  singleMoveToOverflowScalingClassNameIndex,
  singleRibbonOverflowMenuId,
} from "../../Scaling";
import type { SpanningOffsetDict } from "../../utilities";
import {
  checkRibbonOffsetSpaceAvailable,
  getComponentsToMeasure,
  resetComponentMaps,
} from "../../utilities";
import type {
  RibbonControlGroupDefinitionSetSLR,
  RibbonMode,
} from "../Ribbon/Ribbon.Props";
import type { RibbonControlGroupBase } from "../RibbonControlGroup";
import { RibbonControlGroup } from "../RibbonControlGroup";
import { addLayoutCacheKeyToRibbonState } from "./addLayoutCacheKeyToRibbonState";
import {
  estimateRibbonScalingSteps,
  getMoveToOverflowScalingClassNameForScalingIndex,
  getScalingClassName,
  scaleRibbonDownWithBuildTimeScaling,
  scaleRibbonDownWithEstimatedWidth,
  scaleRibbonUpWithEstimatedWidth,
} from "./QuickScaling";
import {
  bottomBarTransitionDurationMs,
  getRibbonBottomBarClassNames,
  getRibbonGroupDividerClassNames,
  modeSwitchTransitionClassNames,
  tabSwitchTransitionClassNames,
} from "./RibbonBottomBar.classNames";
import type { RibbonBottomBarProps } from "./RibbonBottomBar.Props";
import {
  addScalingStepsToCache,
  batchScaleRibbon,
  getCachedInitialScalingStepsToApply,
  getDefaultScalingSteps,
  getInitialScalingState,
  scaleRibbonDown,
  scaleRibbonUp,
} from "./ribbonBottomBarScaling";
import type {
  FullBuildTimeControlWidths,
  GetScaleDownFunctionOptions,
  RibbonScalingState,
  RibbonScalingStateSLRWithCacheKeys,
  ScaledRibbonGroupBase,
  ScaledRibbonGroupMLR,
  ScaledRibbonGroupSLR,
  RibbonScalingSafeguardState,
} from "./ribbonBottomBarScalingTypes";
import {
  CoreScalingState,
  MeasureFallbackState,
  ApplyAdditionalStepsState,
} from "./ribbonBottomBarScalingTypes";
import {
  setCustomRibbonBottomBarReservedSpace,
  getRibbonBottomBarStylingConstants,
  setCustomRibbonBottomBarStylingConstants,
} from "../../ribbonStylingConstants";
import { renderModeChevron } from "../RibbonModeChevron/RibbonModeChevron";
import { ribbonBottomBarContainerClassname } from "../../utilities/consts";
import {
  estimatedScalingWidths,
  setEstimatedScalingWidths,
} from "./estimatedWidths";
import { ReactUiPaintTiming } from "@1js/acui-paint-timing";

/**
 * With build-time scaling, we will first render the ribbon directly into the scaled state. At some point after boot, we are going to replace the build-time scaling logic with todays scaling code.
 * We need to do this since there is a possibility of delay-rendering additional controls that we can't measure at boot such as add-ins, server-based controls, additional tabs, etc.
 * Adding an AppResizeGroup after boot would cause a visible flicker on the screen, since it would have to hide all content for measurement. In order to prevent this, we are going to use the
 * 'measureContentVisibly' prop on the AppResizeGroup. This will allow for a seamless transition from build-time scaling to standard scaling after the boot process is complete.
 * An added benefit of this approach is that if we hit an edge case where build-time scaling is wrong and renders too much content, the AppResizeGroup will immediately notice this and
 * apply additional scaling steps until it fits.
 * The transition from measureContentVisibly to !measureContentVisibly requires us to ignore cached values and calculate the content widths as if it was the first render, so we need to use
 * the following completedResizeGroupTransition variable to make sure cached measurements are ignored until the transition is complete
 */
let completedResizeGroupTransition = false;
let knownControlWidths: FullBuildTimeControlWidths | undefined = undefined;

const getScaleDownFunctionWithCacheKey = memoizeFunction(
  (opts: GetScaleDownFunctionOptions) => {
    return (
      scalingState: RibbonScalingStateSLRWithCacheKeys,
      measuredDimension?: number
    ) => {
      const { quickScalingIsEnabled, mode, safeguardState } = opts;
      /* All scaling steps for the given tab are estimated at once because we know that the DOM contents match the scalingState */
      if (
        quickScalingIsEnabled &&
        Object.keys(estimatedScalingWidths).length === 0 &&
        measuredDimension &&
        mode &&
        safeguardState?.type !== ApplyAdditionalStepsState // In this state, we've manually modified this dictionary because it was first computed incorrectly.
      ) {
        setEstimatedScalingWidths(
          estimateRibbonScalingSteps(
            scalingState,
            measuredDimension,
            mode,
            opts.appContext
          )
        );
      }

      const nextState = quickScalingIsEnabled
        ? scaleRibbonDownWithEstimatedWidth(
            scalingState,
            estimatedScalingWidths
          )
        : scaleRibbonDown(scalingState);

      if (nextState === undefined) {
        return undefined;
      }

      return addLayoutCacheKeyToRibbonState(nextState, safeguardState);
    };
  }
);

const getScaleUpFunctionWithCacheKey = memoizeFunction(
  (quickScalingIsEnabled: boolean, initialStepCountToApply?: number) => {
    let stepCountToApply = initialStepCountToApply;
    return (scalingState: RibbonScalingStateSLRWithCacheKeys) => {
      if (scalingState.appliedScalingStepCount === stepCountToApply) {
        stepCountToApply = undefined;
        return undefined;
      }
      const nextState = quickScalingIsEnabled
        ? scaleRibbonUpWithEstimatedWidth(scalingState, estimatedScalingWidths)
        : scaleRibbonUp(scalingState);

      if (nextState === undefined) {
        return undefined;
      }
      return addLayoutCacheKeyToRibbonState(nextState);
    };
  }
);

const batchScaleRibbonWithCacheKey = (
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  scalingStepsToApply: number
) => {
  const nextState = batchScaleRibbon(scalingState, scalingStepsToApply);
  if (nextState === undefined) {
    return undefined;
  }
  return addLayoutCacheKeyToRibbonState(nextState);
};

export const ribbonBottomBarContainerId = "outerRibbonContainer";
export const innerRibbonContainerId = "innerRibbonContainer";

export class RibbonBottomBar extends React.PureComponent<
  RibbonBottomBarProps,
  {}
> {
  public static displayName = "RibbonBottomBar";
  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;
  private focusZone = React.createRef<FocusZone>();
  private bottomBarTabsterRef: React.RefObject<{ focus: () => void }> =
    React.createRef();
  private tabsterFocusIsEnabled: boolean = false;
  private ribbonControlGroups: RibbonControlGroupBase[] = [];
  private resizeGroupHasRenderedData = false;
  private ribbonMode?: RibbonMode;
  private spanningOffsetDict: SpanningOffsetDict = {};
  private paddleContainer = React.createRef<
    AppOverflowPaddleControl | AppAutoOverflowPaddleControl
  >();
  private shouldForceClassNameRecalc: boolean = false;
  private lastKnownClassName: string = "";
  private shouldRecomputeClassName = true;
  private lastKnownRibbonData: RibbonScalingStateWithCacheKeys | undefined =
    undefined;
  private forceResponsiveScalingOff = false;
  private reservedSpace: number = 0;
  private scalingSafeguardState: RibbonScalingSafeguardState | undefined =
    undefined;
  private ribbonScalingSafeguardEnabled: boolean = false;
  private deterministicRibbonScalingEnabled: boolean = false;
  private responsiveContainer = React.createRef<HTMLDivElement>();
  private renderedRibbons: InitialTabRenderRecords = {
    SingleLine: {},
    Multiline: {},
  };
  private trackOOUXAsyncUiEnabled: boolean = false;
  private alternatePaintTiming = false;

  private get knownWidths(): FullBuildTimeControlWidths {
    knownControlWidths = knownControlWidths ?? {
      SingleLine: {},
      Multiline: {},
    };

    return knownControlWidths;
  }

  private set knownWidths(knownWidths: FullBuildTimeControlWidths | undefined) {
    if (!knownControlWidths) {
      knownControlWidths = { SingleLine: {}, Multiline: {} };
    }

    knownControlWidths.SingleLine =
      knownWidths?.SingleLine || knownControlWidths.SingleLine;
    knownControlWidths.Multiline =
      knownWidths?.Multiline || knownControlWidths.Multiline;
  }
  constructor(
    props: RibbonBottomBarProps,
    context: React.ContextType<typeof AppContext>
  ) {
    super(props, context);
    // The AppResizeGroup doesn't need to transition if it isn't rendered using build-time scaling
    completedResizeGroupTransition = !props.buildTimeControlWidths;
    this.ribbonScalingSafeguardEnabled =
      !!context?.isExperimentalFeatureEnabled?.(
        ribbonExperiments.ribbonScalingSafeguard
      );
    this.deterministicRibbonScalingEnabled =
      !!context?.isExperimentalFeatureEnabled?.(
        ribbonExperiments.deterministicRibbonScaling
      ) ||
      !!context?.isExperimentalFeatureEnabled?.(
        ribbonExperiments.deterministicRibbonScalingSetting
      );
    this.trackOOUXAsyncUiEnabled = !!context?.isExperimentalFeatureEnabled?.(
      ribbonExperiments.trackOOUXAsyncUiEnabled
    );
    this.setDefaultScalingSafeguardState();
  }

  /**
   * When the component props update, make sure the innerRef is updated.
   */
  public componentDidUpdate(prevProps: Readonly<RibbonBottomBarProps>): void {
    if (prevProps.innerRef === this.props.innerRef) {
      return;
    }

    if (prevProps.innerRef) {
      prevProps.innerRef.current = null;
    }

    if (this.props.innerRef) {
      this.props.innerRef.current = this;
    }
  }

  /**
   * When the component has mounted, update the innerRef.
   */
  public componentDidMount(): void {
    if (this.props.innerRef) {
      this.props.innerRef.current = this;
    }
  }

  public componentWillUnmount() {
    this.ribbonControlGroups = [];
  }

  /**
   * checks to display the ribbon mode chevron
   */
  private showRibbonModeChevron(): boolean {
    return (
      !!this.props.ribbonModeToggleProps?.onRibbonModeToggle ||
      isShyRibbonEnabled(this.context)
    );
  }

  private forceRerender = () => {
    // We only need to rerender if the paddles are not visible
    if (
      this.props.paddleProps?.disable &&
      this.paddleContainer.current?.isPaddlesVisible()
    ) {
      return;
    }

    const className = this.lastKnownRibbonData
      ? getScalingClassName(
          {
            scalingState: this.lastKnownRibbonData,
            knownWidths: this.knownWidths[this.props.controlGroups.type],
            reservedSpace: this.reservedSpace,
            forceRecalc: true,
            reactMLRIsEnabled: !!this.context.isExperimentalFeatureEnabled?.(
              ribbonExperiments.multilineRibbonIsEnabled
            ),
            ensureFullWidths: !!this.props.ensureFullControlWidths,
            appContext: this.context,
          },
          mergeStyles
        )
      : "";

    // If the classnames are the same, we do not need to rerender
    if (className !== this.lastKnownClassName) {
      this.lastKnownClassName = className;

      // If we have a ref to the responsiveContainer, just update
      // the className and we are done, otherwise we need to fully rerender
      if (className && this.responsiveContainer.current) {
        this.responsiveContainer.current.className = className;
      } else {
        this.shouldRecomputeClassName = false;
        this.forceUpdate();
        this.shouldForceClassNameRecalc = true;
      }
    } else {
      this.shouldForceClassNameRecalc = false;
    }
  };

  /** To measure the AsyncUI time taken for every Tab Switch, we
    need a new paint timing element for every tab switch. Using just
    the interactivityTrackerContext will not work as the paint
    timing element will be reused for every tab switch. To avoid
    this, we are using a new paint timing element for every tab
    switch by alternating between two paint timing elements. This is
    done by manipulating the hidden prop which removes the
    ReactUiPaintTiming element when true */
  private elementTimingNode = (id: string): JSX.Element => {
    return (
      <>
        <ReactUiPaintTiming
          elementTimingId={"TabSwitch"}
          interactivityTrackerContext={`TabSwitch_${id}`}
          hidden={this.alternatePaintTiming}
        />
        <ReactUiPaintTiming
          elementTimingId={"TabSwitch"}
          interactivityTrackerContext={`TabSwitch_${id}`}
          hidden={!this.alternatePaintTiming}
        />
      </>
    );
  };

  public render() {
    const {
      ariaLabeledBy,
      controlGroups,
      id,
      initialScalingSteps,
      isSpanning,
      disableQuickScaling,
      ribbonModeToggleProps,
      scalingSteps,
      controlRendererFunction,
      controlInMenuRendererFunction,
      appStrings,
      onError,
      disableAnimations,
      onReduceData,
      buildTimeControlWidths,
      onWarning,
      customStylingConstants,
      customStyles,
      reservedSpace,
      ariaControlsId,
    } = this.props;

    // Set the known widths if we have not yet
    if (!knownControlWidths && !this.isBuildTimeScalingForcedOff()) {
      this.knownWidths = buildTimeControlWidths;
    } else if (
      this.deterministicRibbonScalingEnabled &&
      !this.isBuildTimeScalingForcedOff()
    ) {
      // Pull in the dynamically-generated widths
      this.knownWidths = {
        SingleLine: {
          ...this.knownWidths.SingleLine,
          ...buildTimeControlWidths?.SingleLine,
        },
        Multiline: {
          ...this.knownWidths.Multiline,
          ...buildTimeControlWidths?.Multiline,
        },
      };
    }

    const { isExperimentalFeatureEnabled, isPlatformFeatureEnabled } =
      this.context;
    const isMLREnabled = isExperimentalFeatureEnabled?.(
      ribbonExperiments.multilineRibbonIsEnabled
    );
    const shouldForceClassNameRecalc = this.shouldForceClassNameRecalc;
    this.shouldForceClassNameRecalc = false;
    this.reservedSpace = reservedSpace ?? 0;
    setCustomRibbonBottomBarStylingConstants(customStylingConstants);
    setCustomRibbonBottomBarReservedSpace(this.reservedSpace);

    if (controlGroups.type === "Multiline" && !isMLREnabled) {
      throw new Error(
        "Can't render a multi-line ribbon while the multilineRibbonIsEnabled experiment is off"
      );
    }
    // Each time we render the ribbon, we should clear out what control groups we have as they may change
    // and we need to make sure we have an accurate list.
    this.ribbonControlGroups = [];

    const cachedScalingIsEnabled = isExperimentalFeatureEnabled?.(
      ribbonExperiments.ribbonCachedScalingIsEnabled
    );
    const responsiveRibbonScalingIsEnabled =
      this.responsiveRibbonScalingIsActive(controlGroups.type);
    const cachedScalingUpAllowed = isPlatformFeatureEnabled?.(
      platformFeatures.cachedScalingUpAllowed
    );
    const initialStepCountToApply = cachedScalingIsEnabled
      ? getCachedInitialScalingStepsToApply(
          controlGroups.type,
          id,
          this.reservedSpace,
          onError
        )
      : undefined;

    const initialState = getInitialScalingState(
      id,
      controlGroups,
      scalingSteps || getDefaultScalingSteps(controlGroups),
      initialScalingSteps,
      controlRendererFunction,
      controlInMenuRendererFunction,
      initialStepCountToApply,
      responsiveRibbonScalingIsEnabled,
      responsiveRibbonScalingIsEnabled &&
        isExperimentalFeatureEnabled?.(
          "SharedOnline.ChangeGate.OverscalingSafeguardRS"
        ),
      cachedScalingUpAllowed
    );
    if (this.ribbonMode !== initialState.type) {
      this.ribbonMode = initialState.type;
      resetComponentMaps();
      this.spanningOffsetDict = {};
    }

    /* WI#4080055 tracks estimated scaling for spanning scenarios */
    const quickScalingIsEnabled = !disableQuickScaling && !isSpanning;

    let dataNeedsMeasuring =
      !!this.props.isSpanning || !completedResizeGroupTransition; //||
    const initialRibbonData = addLayoutCacheKeyToRibbonState(initialState);

    /* If the ribbon's cacheKey changes, then the measurements that we've stored are no longer valid */
    if (
      !this.lastKnownRibbonData ||
      initialRibbonData.cacheKey !== this.lastKnownRibbonData.cacheKey
    ) {
      this.lastKnownRibbonData = initialRibbonData;
      setEstimatedScalingWidths({});
      dataNeedsMeasuring = dataNeedsMeasuring || quickScalingIsEnabled;
      // Reset scaling state with the new layout
      this.setDefaultScalingSafeguardState();
    }

    const avoidResizeGroup = responsiveRibbonScalingIsEnabled;

    const measureContentVisibly =
      !!buildTimeControlWidths?.[controlGroups.type] &&
      !this.isBuildTimeScalingForcedOff();
    const bottomBarResizeGroup = (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getRibbonBottomBarClassNames(theme, {
            ribbonMode: controlGroups.type,
            isModeChevronVisible: this.showRibbonModeChevron(),
            reduceAnimations: isReducedAnimations() || disableAnimations,
            customStyles,
          });
          return avoidResizeGroup ? (
            this.renderScaledGroups(theme)(initialRibbonData)
          ) : (
            <AppResizeGroup
              id={id}
              className={styles.resizeGroup}
              onRenderData={this.renderScaledGroups(theme)}
              onReduceData={
                onReduceData ??
                getScaleDownFunctionWithCacheKey({
                  quickScalingIsEnabled,
                  measureContentVisibly,
                  onError,
                  mode: initialRibbonData.type,
                  appContext: this.context,
                  safeguardState: this.scalingSafeguardState,
                })
              }
              onGrowData={getScaleUpFunctionWithCacheKey(
                quickScalingIsEnabled,
                cachedScalingUpAllowed ? initialStepCountToApply : undefined
              )}
              onBatchScaleData={batchScaleRibbonWithCacheKey}
              data={
                measureContentVisibly
                  ? scaleRibbonDownWithBuildTimeScaling(
                      initialRibbonData,
                      buildTimeControlWidths[controlGroups.type],
                      !!isExperimentalFeatureEnabled?.(
                        ribbonExperiments.multilineRibbonIsEnabled
                      ),
                      this.reservedSpace,
                      onWarning,
                      controlGroups.type,
                      this.context
                    )
                  : initialRibbonData
              }
              onFocus={this.onFocus}
              isScalingComplete={
                isSpanning ? this.isScalingComplete : undefined
              }
              dataNeedsMeasuring={dataNeedsMeasuring}
              onAfterScalingComplete={this.measureRenderedData}
              measureContentVisibly={measureContentVisibly}
            />
          );
        }}
      </AppThemeConsumer>
    );

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const styles = getRibbonBottomBarClassNames(theme, {
            ribbonMode: controlGroups.type,
            isModeChevronVisible: this.showRibbonModeChevron(),
            reduceAnimations: isReducedAnimations() || disableAnimations,
            customStyles,
          });

          const ribbonContents = () => {
            const bottomBarContent =
              !responsiveRibbonScalingIsEnabled &&
              !this.props.paddleProps?.disable &&
              this.props.paddleProps ? (
                <AppOverflowPaddleControl
                  ref={
                    this
                      .paddleContainer as React.RefObject<AppOverflowPaddleControl>
                  }
                  downChevronIcon={this.props.paddleProps.ribbonPaddlesIcon}
                  disableModeTogglePadding={
                    this.props.paddleProps.disableModeTogglePadding ?? false
                  }
                  rightRibbonPaddleLabel={appStrings.rightRibbonPaddleLabel}
                  leftRibbonPaddleLabel={appStrings.leftRibbonPaddleLabel}
                  onPaddlesShown={this.props.paddleProps.onPaddlesShown}
                  onPaddleExecuted={this.props.paddleProps.onPaddleExecuted}
                  innerChildContainerId={innerRibbonContainerId}
                  onContentOverflow={
                    this.ribbonScalingSafeguardEnabled
                      ? this.onContentOverflow.bind(this)
                      : undefined
                  }
                >
                  {bottomBarResizeGroup}
                </AppOverflowPaddleControl>
              ) : (
                bottomBarResizeGroup
              );

            let contents = (
              <div
                className={styles.tabContentContainer}
                id={ribbonBottomBarContainerId}
              >
                {this.trackOOUXAsyncUiEnabled && this.elementTimingNode(id)}
                {bottomBarContent}
              </div>
            );

            if (responsiveRibbonScalingIsEnabled) {
              // Check if we have rendered the current tab in the current ribbonMode
              // to determine if we can minimize style calculation work
              const initialRender = this.deterministicRibbonScalingEnabled
                ? false
                : !this.renderedRibbons[initialState.type][initialState.id];
              this.renderedRibbons[initialState.type][initialState.id] = true;

              const useModifiedEstimatedWidths =
                this.deterministicRibbonScalingEnabled &&
                this.scalingSafeguardState?.type === ApplyAdditionalStepsState;

              if (
                !this.responsiveContainer.current ||
                this.shouldRecomputeClassName
              ) {
                this.lastKnownClassName = getScalingClassName(
                  {
                    scalingState: initialRibbonData,
                    knownWidths: this.knownWidths[controlGroups.type],
                    reservedSpace: this.reservedSpace,
                    forceRecalc:
                      shouldForceClassNameRecalc || useModifiedEstimatedWidths,
                    reactMLRIsEnabled: !!isExperimentalFeatureEnabled?.(
                      ribbonExperiments.multilineRibbonIsEnabled
                    ),
                    ensureFullWidths: !!this.props.ensureFullControlWidths,
                    initialRender,
                    appContext: this.context,
                    modifiedEstimatedWidths: useModifiedEstimatedWidths
                      ? estimatedScalingWidths
                      : undefined,
                    setGlobalEstimatedWidths:
                      this.deterministicRibbonScalingEnabled,
                  },
                  mergeStyles
                );
              }
            }

            // We should always recompute the className by default
            this.shouldRecomputeClassName = true;

            contents = responsiveRibbonScalingIsEnabled ? (
              <OverflowNotifier
                onOverflow={this.forceRerender}
                onError={this.onResponsiveScalingError}
                reservedSpace={this.reservedSpace}
              >
                <div
                  ref={this.responsiveContainer}
                  className={this.lastKnownClassName}
                >
                  {contents}
                </div>
              </OverflowNotifier>
            ) : (
              contents
            );

            const offsetWidth = chevronWidth + tabContentContainerPadding;

            return responsiveRibbonScalingIsEnabled &&
              this.props.paddleProps &&
              !this.props.paddleProps.disable ? (
              <AppAutoOverflowPaddleControl
                ref={
                  this
                    .paddleContainer as React.RefObject<AppAutoOverflowPaddleControl>
                }
                downChevronIcon={this.props.paddleProps.ribbonPaddlesIcon}
                disableModeTogglePadding={
                  this.props.paddleProps.disableModeTogglePadding ?? false
                }
                rightRibbonPaddleLabel={appStrings.rightRibbonPaddleLabel}
                leftRibbonPaddleLabel={appStrings.leftRibbonPaddleLabel}
                onPaddlesShown={this.props.paddleProps.onPaddlesShown}
                onPaddleExecuted={this.props.paddleProps.onPaddleExecuted}
                measuringOffset={offsetWidth}
                onContentOverflow={
                  this.ribbonScalingSafeguardEnabled
                    ? this.onContentOverflow.bind(this)
                    : undefined
                }
                expediteOverflowHandling={this.ribbonScalingSafeguardEnabled}
              >
                {contents}
              </AppAutoOverflowPaddleControl>
            ) : (
              contents
            );
          };

          const { isExperimentalFeatureEnabled } = this.context;
          const shyRibbonIsEnabled = isShyRibbonEnabled(this.context);
          const renderAsFlyoutForFeature =
            isChatPPTEnabled(this.context) || shyRibbonIsEnabled;
          const renderAsFlyout = renderAsFlyoutForFeature;

          const ribbonContainerTabsterFocusEnabled =
            !!this.context?.isExperimentalFeatureEnabled?.(
              ribbonExperiments.fluentV9TabListSpinButtonFocusIsEnabled
            );
          this.tabsterFocusIsEnabled =
            ribbonContainerTabsterFocusEnabled ||
            !!this.props.useTabsterTabControl;

          const attributes = this.tabsterFocusIsEnabled
            ? useCommonArrowNavGroup()
            : undefined;

          const tabPanelContents = (
            <div
              className={`${styles.root} ${ribbonBottomBarContainerClassname}`}
              aria-labelledby={ariaLabeledBy}
              role="tabpanel"
              id={ariaControlsId}
              {...attributes}
            >
              {ribbonContents()}
              {ribbonModeToggleProps &&
                this.showRibbonModeChevron() &&
                renderModeChevron(
                  initialRibbonData.type,
                  ribbonModeToggleProps,
                  appStrings.ribbonModeChevronLabel,
                  getRibbonBottomBarStylingConstants(initialRibbonData.type)
                    .bottomBarHeight,
                  appStrings,
                  controlInMenuRendererFunction,
                  {
                    onModeChevronFocus: this.onModeChevronFocus,
                    preventMenuExecution: this.props.preventMenuExecution,
                    customStyles: customStyles?.chevronContainer,
                    shyRibbonIsEnabled,
                    renderAsFlyout,
                    disableAnimations,
                  }
                )}
            </div>
          );

          // Alternate Paint Timing Ids for every tab switch
          this.alternatePaintTiming = !this.alternatePaintTiming;

          return this.tabsterFocusIsEnabled ? (
            <DivWithTabsterAttributes
              firstFocusableRef={this.bottomBarTabsterRef}
              onKeyDown={this.onKeyDown}
              useTabsterTabControl={this.props.useTabsterTabControl}
              className={styles.outerContainer}
              axis={this.props.tabsterAxis}
              onActiveElementChanged={this.onActiveElementChanged}
              moveFocusRemoved={true}
            >
              {tabPanelContents}
            </DivWithTabsterAttributes>
          ) : (
            <FocusZone
              key={`${id}-fz`}
              componentRef={this.focusZone}
              direction={FocusZoneDirection.horizontal}
              data-is-focusable={true}
              handleTabKey={FocusZoneTabbableElements.inputOnly}
              isCircularNavigation={true}
              allowFocusRoot={true}
              onKeyDown={this.onKeyDown}
              doNotAllowFocusEventToPropagate={doNotAllowFocusEventToPropagate}
              onActiveElementChanged={this.onActiveElementChanged}
              className={styles.outerContainer}
            >
              {tabPanelContents}
            </FocusZone>
          );
        }}
      </AppThemeConsumer>
    );
  }

  private onFocus = () => {
    const { onFocus } = this.props;
    if (onFocus) {
      onFocus();
    }
  };

  public focus = (): boolean => {
    if (this.tabsterFocusIsEnabled && this.bottomBarTabsterRef?.current) {
      this.bottomBarTabsterRef.current.focus();
      return true;
    }

    if (this.focusZone.current) {
      this.focusZone.current.focus(
        true /*forceIntoFirstElement*/,
        true /* bypassHiddenElements */
      );
      return true;
    }
    return false;
  };

  private onActiveElementChanged = (element?: HTMLElement) => {
    const { paddleProps } = this.props;

    // If the element we are moving focus to is off the screen, we should scroll it into view
    // and update the paddles.
    if (
      !paddleProps?.disable &&
      this.paddleContainer.current?.isPaddlesVisible()
    ) {
      if (element) {
        this.paddleContainer.current?.scrollActiveElementIntoView(element);
      }

      // Call handleChildOverflow with -1 to signify that this is not a state where the paddles should be hidden
      // (0 or undefined tells AppAutoOverflowPaddleControl that the paddles should be removed)
      this.paddleContainer.current?.handleChildOverflow(
        -1 /* overflowingAmount */
      );
    }
  };

  private measureRenderedData = (ribbonScalingState: RibbonScalingState) => {
    const {
      id,
      onAfterScalingCompleted,
      onError,
      paddleProps,
      buildTimeControlWidths,
    } = this.props;
    this.resizeGroupHasRenderedData = true;

    if (!buildTimeControlWidths) {
      completedResizeGroupTransition = true;
    }

    const scalingStepsExhausted =
      !!ribbonScalingState &&
      ribbonScalingState.appliedScalingStepCount ===
        ribbonScalingState.scalingSteps.length;

    // Attempt to add ribbon paddles if we have exhausted all scaling steps given for
    // the ribbon tab else hide the paddles.
    if (!paddleProps?.disable) {
      if (this.ribbonScalingSafeguardEnabled) {
        this.paddleContainer.current?.handleChildOverflow();
      } else {
        if (scalingStepsExhausted) {
          this.paddleContainer.current?.handleChildOverflow();
        } else {
          this.paddleContainer.current?.hideRibbonPaddles();
        }
      }
    }

    if (
      this.context.isExperimentalFeatureEnabled?.(
        ribbonExperiments.ribbonCachedScalingIsEnabled
      )
    ) {
      addScalingStepsToCache(
        ribbonScalingState.type,
        id,
        ribbonScalingState.appliedScalingStepCount,
        this.reservedSpace,
        onError
      );
    }

    if (!onAfterScalingCompleted) {
      return;
    }

    if (ribbonScalingState.type === "Multiline") {
      onAfterScalingCompleted({
        id: ribbonScalingState.id,
        scalingStepsApplied: ribbonScalingState.appliedScalingStepCount,
        scalingStepsExhausted,
        innerWindowWidth:
          (this.context.appWindow?.innerWidth ?? window.innerWidth) -
          this.reservedSpace,
      });
      return;
    }

    const { scaledRibbonGroups, scaledEndOfRibbonOverflowMenu } =
      ribbonScalingState;
    const shouldRenderSingleOverflowMenu =
      scaledEndOfRibbonOverflowMenu.sections.length !== 0;

    let itemsInOverflows = 0;
    if (!shouldRenderSingleOverflowMenu) {
      itemsInOverflows = scaledRibbonGroups.reduce(
        (acc: number, group: ScaledRibbonGroupSLR) =>
          acc + group.overflowControls.length,
        0
      );
    } else {
      const itemsInOwnOverflowsCount = scaledRibbonGroups.reduce(
        (acc: number, group: ScaledRibbonGroupSLR) =>
          acc + (group.hasOwnOverflow ? group.overflowControls.length : 0),
        0
      );
      const itemsInSingleOverflowCount =
        scaledEndOfRibbonOverflowMenu.sections.reduce(
          (acc: number, section: MenuSection) => acc + section.controls.length,
          0
        );
      itemsInOverflows = itemsInOwnOverflowsCount + itemsInSingleOverflowCount;
    }

    onAfterScalingCompleted({
      id: ribbonScalingState.id,
      itemsInOverflows,
      scalingStepsApplied: ribbonScalingState.appliedScalingStepCount,
      scalingStepsExhausted,
      innerWindowWidth:
        (this.context.appWindow?.innerWidth ?? window.innerWidth) -
        this.reservedSpace,
    });
  };

  private getKeytipPropsForRibbonOverflowMenu = (
    keytipContent: string
  ): IKeytipProps => {
    let keytipProps: IKeytipProps =
      this.generateKeytipPropsFromContent(keytipContent);
    const [firstGroup] = this.props.controlGroups.groups;
    if (firstGroup && firstGroup.keytipProps) {
      keytipProps = this.mergeKeySequencesWithFirstGroup(
        firstGroup.keytipProps.keySequences,
        keytipProps
      );
    }
    return keytipProps;
  };

  private generateKeytipPropsFromContent = memoizeFunction(
    (keytipContent: string) => {
      return {
        content: keytipContent,
        hasMenu: true,
        keySequences: [keytipContent.toLocaleLowerCase()],
      };
    }
  );

  private mergeKeySequencesWithFirstGroup = memoizeFunction(
    (firstGroupSequences: string[], keytipProps: IKeytipProps) => {
      // Add the first item in firstGroupSequences keytipProps (which is the tab header keytip).
      // This will create the correct full sequence for the overflow button keytip
      const newProps = { ...keytipProps };
      newProps.keySequences = [
        ...firstGroupSequences.slice(0, -1),
        ...keytipProps.keySequences,
      ];
      return newProps;
    }
  );

  private renderRibbonOverflowMenu = (
    scaledEndOfRibbonOverflowMenu: MenuDefinition,
    ribbonScalingState: RibbonScalingState,
    isMeasured: boolean
  ): JSX.Element => {
    if (ribbonScalingState.type === "Multiline") {
      throw new Error("Cannot render overflow menus in MLR");
    }

    const { onOverflowMenuOpen, onOverflowMenuClose } =
      getRibbonOverflowMenuOpenAndCloseCommandHandler(
        ribbonScalingState.ribbonGroupDefinitions
      );

    const {
      appStrings,
      overflowMenuLauncherIcon,
      controlRendererFunction,
      controlInMenuRendererFunction,
      disableAnimations,
      deferredOverflowMenuAs,
      preventMenuExecution,
      customStyles,
      preventShadowRendering,
    } = this.props;

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const classNames = getRibbonBottomBarClassNames(theme, {
            ribbonMode: ribbonScalingState.type,
            reduceAnimations: isReducedAnimations() || disableAnimations,
            customStyles,
          });
          return (
            <div className={classNames.groupContainer}>
              <RibbonControlGroup
                type={"SingleLine"}
                ref={
                  !isMeasured
                    ? (elem: RibbonControlGroup) => {
                        this.ribbonControlGroups.push(elem);
                      }
                    : undefined
                }
                multiMenuOverflowControls={
                  scaledEndOfRibbonOverflowMenu.sections
                }
                overflowMenuLauncherLabel={
                  appStrings.bottomBarOverflowMenuLabel || ""
                }
                id={singleRibbonOverflowMenuId}
                onOverflowMenuOpen={onOverflowMenuOpen}
                onOverflowMenuClose={onOverflowMenuClose}
                controlRendererFunction={controlRendererFunction}
                controlInMenuRendererFunction={controlInMenuRendererFunction}
                overflowMenuLauncherIcon={overflowMenuLauncherIcon}
                keytipProps={
                  appStrings.overflowWellKeytip
                    ? this.getKeytipPropsForRibbonOverflowMenu(
                        appStrings.overflowWellKeytip
                      )
                    : undefined
                }
                groupLabel={appStrings.bottomBarOverflowMenuLabel || ""}
                deferredOverflowMenuAs={deferredOverflowMenuAs}
                preventMenuExecution={preventMenuExecution}
                shouldRenderOverflowUnconditionally={this.responsiveRibbonScalingIsActive(
                  "SingleLine"
                )}
                preventShadowRendering={preventShadowRendering}
              />
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  };

  private renderScaledGroups =
    (theme: ITheme) =>
    (ribbonScalingState: RibbonScalingState): JSX.Element => {
      return this.renderScaledGroupsBase(
        ribbonScalingState,
        true /* firstGroup */,
        theme
      );
    };

  private renderScaledGroupsBase = (
    ribbonScalingState: RibbonScalingState,
    firstGroup: boolean,
    theme: ITheme
  ): JSX.Element => {
    const scaledRibbonGroups: ScaledRibbonGroupBase[] =
      ribbonScalingState.scaledRibbonGroups;

    if (
      !firstGroup &&
      (scaledRibbonGroups.length < 1 || scaledRibbonGroups[0] === undefined)
    ) {
      return <></>;
    }

    const { disableAnimations, customStyles } = this.props;
    const reducedAnimations = disableAnimations || isReducedAnimations();
    const responsiveRibbonScalingIsEnabled =
      this.responsiveRibbonScalingIsActive(ribbonScalingState.type);

    const scaledEndOfRibbonOverflowMenu: MenuDefinition | undefined =
      ribbonScalingState.type === "SingleLine"
        ? ribbonScalingState.scaledEndOfRibbonOverflowMenu
        : undefined;

    // We should render a single overflow menu if we have a scaled end of ribbon overflow menu definition
    // and we are responsive scaling or have sections already in the single overflow menu definition
    const shouldRenderSingleOverflowMenu: boolean =
      !!scaledEndOfRibbonOverflowMenu &&
      (responsiveRibbonScalingIsEnabled ||
        scaledEndOfRibbonOverflowMenu.sections.length !== 0);

    // Don't animate controls while ResizeGroup is doing determining the initial content to render.
    const animateControlGroups =
      !reducedAnimations && this.resizeGroupHasRenderedData;
    const getDividerClassNames =
      (getWrapperClass?: () => string | undefined) => () =>
        getRibbonGroupDividerClassNames(
          theme,
          getRibbonBottomBarStylingConstants(this.props.controlGroups.type),
          getWrapperClass && getWrapperClass()
        );

    // if the ribbon mode toggle is being shown, we want to draw a vertical divider after the last group
    const numberOfGroupsToRenderDivider = this.showRibbonModeChevron()
      ? scaledRibbonGroups.length
      : scaledRibbonGroups.length - 1;

    // When responsive scaling is on, we need to add the onFocus event to the RibbonBottomBarContainer
    // so that it bubbles up to track the tab order/focus correctly
    const addOnFocus = responsiveRibbonScalingIsEnabled;

    const coreControlGroupContent = (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const classNames = getRibbonBottomBarClassNames(theme, {
            ribbonMode: this.props.controlGroups.type,
            reduceAnimations: reducedAnimations,
            customStyles,
          });
          return (
            <MeasuredContext.Consumer>
              {({ isMeasured }) => (
                <div
                  className={classNames.bottomBarContainer}
                  data-automation-type={
                    this.props.dataAutomationType || "RibbonBottomBarContainer"
                  }
                  data-unique-id={
                    this.props.dataUniqueId || "RibbonBottomBarContainer"
                  }
                  id={innerRibbonContainerId}
                  onFocus={addOnFocus ? this.onFocus : undefined}
                >
                  {scaledRibbonGroups.map((group, index) => (
                    <div key={group.id} className={classNames.groupContainer}>
                      {group.type === "SingleLine"
                        ? this.renderControlGroupSLR(
                            group as ScaledRibbonGroupSLR,
                            shouldRenderSingleOverflowMenu &&
                              !(group as ScaledRibbonGroupSLR).hasOwnOverflow,
                            !isMeasured && animateControlGroups,
                            ribbonScalingState.id,
                            isMeasured
                          )
                        : this.renderControlGroupMLR(
                            group as ScaledRibbonGroupMLR,
                            !isMeasured && animateControlGroups,
                            ribbonScalingState.id,
                            isMeasured
                          )}
                      {(index < numberOfGroupsToRenderDivider ||
                        shouldRenderSingleOverflowMenu) && (
                        <VerticalDivider
                          getClassNames={getDividerClassNames(
                            group.type === "SingleLine" &&
                              responsiveRibbonScalingIsEnabled
                              ? getGroupDividerScalingStepClassName(
                                  group as ScaledRibbonGroupSLR,
                                  ribbonScalingState.scalingSteps
                                )
                              : undefined
                          )}
                        />
                      )}
                    </div>
                  ))}
                  {shouldRenderSingleOverflowMenu && (
                    <div>
                      {this.renderRibbonOverflowMenu(
                        scaledEndOfRibbonOverflowMenu!,
                        ribbonScalingState,
                        isMeasured
                      )}
                      {this.showRibbonModeChevron() && (
                        <VerticalDivider
                          getClassNames={getDividerClassNames(
                            responsiveRibbonScalingIsEnabled
                              ? this.getSingleOverflowDividerScalingStepClassName()
                              : undefined
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </MeasuredContext.Consumer>
          );
        }}
      </AppThemeConsumer>
    );

    return !(
      reducedAnimations ||
      responsiveRibbonScalingIsEnabled ||
      this.tabsterFocusIsEnabled
    ) ? (
      <MeasuredContext.Consumer>
        {({ isMeasured }) => (
          <TransitionGroup>
            <CSSTransition
              enter={!reducedAnimations && !isMeasured}
              exit={!reducedAnimations && !isMeasured}
              key={ribbonScalingState.id}
              timeout={bottomBarTransitionDurationMs}
              classNames={tabSwitchTransitionClassNames}
            >
              <TransitionGroup component={null}>
                <CSSTransition
                  enter={!reducedAnimations && !isMeasured}
                  exit={!reducedAnimations && !isMeasured}
                  key={`${ribbonScalingState.type}`}
                  timeout={bottomBarTransitionDurationMs}
                  classNames={modeSwitchTransitionClassNames}
                >
                  {coreControlGroupContent}
                </CSSTransition>
              </TransitionGroup>
            </CSSTransition>
          </TransitionGroup>
        )}
      </MeasuredContext.Consumer>
    ) : (
      coreControlGroupContent
    );
  };

  // The goal here is to drop the divider via css alongside the last control in the associated group that drops
  /**
   * Determine the className for the single overflow divider (if needed)
   * @param scalingSteps The current scaling steps
   * @returns Returns a function for getting the className that corresponds when the single overflow should be shown/hidden, or undefined if there is no overflow
   */
  private getSingleOverflowDividerScalingStepClassName =
    (): (() => string | undefined) => () => {
      return getMoveToOverflowScalingClassNameForScalingIndex(
        singleMoveToOverflowScalingClassNameIndex
      );
    };

  private renderControlGroup = (props: any) => {
    const ControlGroupRenderer =
      this.props.ribbonControlGroupRenderer || RibbonControlGroup;
    return <ControlGroupRenderer {...props} />;
  };

  private renderControlGroupSLR = (
    group: ScaledRibbonGroupSLR,
    shouldRenderSingleOverflowMenu: boolean,
    animate: boolean,
    tabId: string,
    isMeasured: boolean
  ): JSX.Element => {
    const responsiveRenderingEnabled =
      this.responsiveRibbonScalingIsActive("SingleLine");
    const groupHasOverflow =
      group.hasOwnOverflow || !shouldRenderSingleOverflowMenu;
    return this.renderControlGroup({
      type: group.type,
      componentRef:
        !isMeasured &&
        ((elem: RibbonControlGroup) => {
          // When using a store connected control group, we need to get the inner
          // wrapped instance to be able to get at its functions
          // tslint:disable-next-line:no-any
          this.ribbonControlGroups.push(
            (elem as any)?.getWrappedInstance
              ? (elem as any).getWrappedInstance()
              : elem
          );
        }),
      animate,
      topLevelControls: group.topLevelControls,
      overflowControls: groupHasOverflow ? group.overflowControls : undefined,
      shouldRenderOverflowUnconditionally:
        responsiveRenderingEnabled && groupHasOverflow,
      overflowMenuLauncherDisabled: group.overflowMenuLauncherDisabled,
      overflowMenuLauncherLabel: group.overflowMenuLauncherLabel,
      onOverflowMenuOpen: group.onOverflowMenuOpen,
      onOverflowMenuClose: group.onOverflowMenuClose,
      id: group.id,
      controlRendererFunction: this.props.controlRendererFunction,
      controlInMenuRendererFunction: this.props.controlInMenuRendererFunction,
      overflowMenuLauncherIcon: this.props.overflowMenuLauncherIcon,
      keytipProps: group.keytipProps,
      groupLabel: group.groupLabel,
      isRibbonSpanning:
        this.props.isSpanning && this.ribbonMode === "SingleLine",
      deferredOverflowMenuAs: this.props.deferredOverflowMenuAs,
      spanningComponentId: this.spanningOffsetDict[tabId]
        ? this.spanningOffsetDict[tabId].componentId
        : undefined,
      spanningOffset: this.spanningOffsetDict[tabId]
        ? this.spanningOffsetDict[tabId].spanningOffset
        : 0,
      preventMenuExecution: this.props.preventMenuExecution,
      fastDisabled: group.fastDisabled,
      preventShadowRendering: this.props.preventShadowRendering,
    });
  };

  private renderControlGroupMLR = (
    group: ScaledRibbonGroupMLR,
    animate: boolean,
    tabId: string,
    isMeasured: boolean
  ): JSX.Element => {
    return this.renderControlGroup({
      type: group.type,
      animate: animate && !group.disableAnimations,
      componentRef:
        !isMeasured &&
        ((elem: RibbonControlGroup) => {
          // When using a store connected control group, we need to get the inner
          // wrapped instance to be able to get at its functions
          // tslint:disable-next-line:no-any
          this.ribbonControlGroups.push(
            (elem as any)?.getWrappedInstance
              ? (elem as any).getWrappedInstance()
              : elem
          );
        }),
      controls: group.controls,
      id: group.id,
      controlRendererFunction: this.props.controlRendererFunction,
      controlInMenuRendererFunction: this.props.controlInMenuRendererFunction,
      groupLabel: group.groupLabel,
      layout:
        this.responsiveRibbonScalingIsActive("Multiline") &&
        group.responsiveLayout
          ? group.responsiveLayout
          : group.layout,
      groupLauncher: group.groupLauncher,
      hideGroupLabel: group.hideGroupLabel,
      isRibbonSpanning:
        this.props.isSpanning && this.ribbonMode === "Multiline",
      spanningComponentId: this.spanningOffsetDict[tabId]
        ? this.spanningOffsetDict[tabId].componentId
        : undefined,
      spanningOffset: this.spanningOffsetDict[tabId]
        ? this.spanningOffsetDict[tabId].spanningOffset
        : 0,
      fastDisabled: group.fastDisabled,
      preventShadowRendering: this.props.preventShadowRendering,
      forceHideGroupLabelOnly: group.forceHideGroupLabelOnly,
    });
  };

  private onModeChevronFocus = () => {
    if (this.paddleContainer.current?.isEndPaddleVisible()) {
      const ribbonContainer = document.querySelector(`#${this.props.id}`);
      if (ribbonContainer) {
        ribbonContainer.scrollLeft = ribbonContainer.scrollWidth;
        this.paddleContainer.current?.handleChildOverflow();
      }
    }
  };

  private onKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
    let shouldMoveFocus = false;

    const validRibbonControlGroups = this.ribbonControlGroups.filter(
      (group) => group !== null && group.hasContainer()
    );

    // If we got CTRL+Left/RightArrow, prevent default since we
    // are handling it here
    if (
      ev &&
      (ev.which === KeyCodes.right || ev.which === KeyCodes.left) &&
      ev.ctrlKey
    ) {
      const focusedIndex = validRibbonControlGroups.findIndex((group) =>
        group.containsFocus()
      );
      // If at the first group and pressing left, or at the last group and pressing right, let it bubble up to RibbonContainer
      // to move focus to ribbonTopBar
      if (
        (ev.which === KeyCodes.left && focusedIndex === 0) ||
        (ev.which === KeyCodes.right &&
          focusedIndex === validRibbonControlGroups.length - 1)
      ) {
        ev.preventDefault();
        return;
      }

      ev.preventDefault();
      shouldMoveFocus = true;
    }

    // If we did not get CTRL + Left/RightArrow OR we have less than
    // two valid chunks to navigate, we do not need to navigate.
    //
    // After this if check, we know that we are handling a CTRL + left/RightArrow AND
    // we have at least two valid ribbon chunks to attempt to navigate to
    if (!shouldMoveFocus || validRibbonControlGroups.length < 2) {
      return;
    }

    const ribbonControlGroupToFocus = (
      getRTL() ? ev.which === KeyCodes.left : ev.which === KeyCodes.right
    )
      ? this.getGroupWithOffset(
          validRibbonControlGroups,
          this.getNextIndexInGroups
        )
      : this.getGroupWithOffset(
          validRibbonControlGroups,
          this.getPreviousIndexInGroups
        );

    if (ribbonControlGroupToFocus) {
      ribbonControlGroupToFocus.focus();
    }
  };

  private responsiveRibbonScalingIsActive(ribbonMode: RibbonMode) {
    // Force responsive scaling off if needed
    // (e.g. we have run into a error or we do not have known widths)
    const shouldForceResponsiveScalingOff =
      this.forceResponsiveScalingOff ||
      Object.keys(this.knownWidths[ribbonMode]).length === 0 ||
      undefined;

    return isResponsiveRibbonScalingFeatureEnabled(
      ribbonMode,
      this.context.isExperimentalFeatureEnabled,
      shouldForceResponsiveScalingOff
    );
  }

  /**
   * For the given index find the "next" group.
   * Note, in this case "next" includes wrapping to the first group if we are at the last group
   * @param {number} startingIndex The index to find the next index of
   * @param {number} groupsLength The total length of the list the groups
   * @returns {number} the index of next group including wrapping
   */
  private getNextIndexInGroups(
    startingIndex: number,
    groupsLength: number
  ): number {
    return (startingIndex + 1) % groupsLength;
  }

  /**
   * For the given index find the "previous" group.
   * Note, in this case "previous" includes wrapping to the last group if we are at the first group
   * @param {number} startingIndex The index to find the pervious index of
   * @param {number} groupsLength The total length of the list the groups
   * @returns {number} the index of next group including wrapping
   */
  private getPreviousIndexInGroups(
    startingIndex: number,
    groupsLength: number
  ): number {
    return startingIndex > 0 ? startingIndex - 1 : groupsLength - 1;
  }

  /**
   * Attempt to find the previous group for the given element.
   * @param {RibbonControlGroups} ribbonControlGroups the valid groups to look through
   * @param {(currentlyFocusedGroupIndex: number, groupsLength: number) => number} calculateOffSet A function to calculate the offset from the focused index
   * @returns The group at the calculated offset, if found
   */
  private getGroupWithOffset(
    ribbonControlGroups: RibbonControlGroupBase[],
    calcualteOffset: (
      currentlyFocusedGroupIndex: number,
      groupsLength: number
    ) => number
  ): RibbonControlGroupBase | undefined {
    for (let index = 0; index < ribbonControlGroups.length; index = index + 1) {
      if (ribbonControlGroups[index].containsFocus()) {
        // Now that we've found the group that currently contains focus,
        // return the control group that matches the calculated offset
        return ribbonControlGroups[
          calcualteOffset(index, ribbonControlGroups.length)
        ];
      }
    }

    return undefined;
  }

  /**
   * Used to apply extra scaling steps when the spanning offset will overflow the container
   */
  private isScalingComplete = (
    scalingState: RibbonScalingState & { cacheKey: string }
  ) => {
    // If we've previously calculated the spanning state for the current tab and the contents of the tab haven't changed, we can skip the computation
    if (
      this.spanningOffsetDict[this.props.id] &&
      this.spanningOffsetDict[this.props.id].controlGroups ===
        this.props.controlGroups
    ) {
      // If the cacheKey's match, we can stop
      return (
        this.spanningOffsetDict[this.props.id].cacheKey ===
        scalingState.cacheKey
      );
    }

    const elementMap = getComponentsToMeasure();
    for (const elementId in elementMap) {
      let offset = getRectSpanningOffset(
        elementMap[elementId].getBoundingClientRect(),
        getRTL()
      );
      if (offset >= 0) {
        offset += 8; // 8px is the extra buffer that we want on the left side of the control
        if (
          checkRibbonOffsetSpaceAvailable(
            offset,
            document.getElementById(ribbonBottomBarContainerId),
            document.getElementById(innerRibbonContainerId)
          )
        ) {
          this.spanningOffsetDict[this.props.id] = {
            componentId: elementId,
            spanningOffset: offset,
            cacheKey: scalingState.cacheKey,
            controlGroups: this.props.controlGroups,
          };
          return true;
        }
        return false;
      }
    }
    return true;
  };

  private onResponsiveScalingError = (message: string) => {
    this.forceResponsiveScalingOff = true;

    // Fire a warning since we are falling back and not truly in an error state
    this.props.onWarning?.(message);
    this.forceUpdate();
  };

  private setDefaultScalingSafeguardState(): void {
    if (this.ribbonScalingSafeguardEnabled) {
      this.scalingSafeguardState = {
        type: !!this.props.buildTimeControlWidths
          ? CoreScalingState
          : MeasureFallbackState,
        renderCount: 0,
      };
    }
  }

  private isBuildTimeScalingForcedOff(): boolean {
    return (
      this.ribbonScalingSafeguardEnabled &&
      this.scalingSafeguardState?.type !== CoreScalingState &&
      !this.deterministicRibbonScalingEnabled
    );
  }

  /**
   * Called when ribbon content has overflowed. Check if we need to update the scaling state
   * @param {number} width the width of the rendered content
   * @param {number} offset the width of the content rendered outside of the designated area
   * @returns true if the ribbon can take action to update the content, false if nothing else can be done
   */
  private onContentOverflow(width: number, offset: number): boolean {
    const currentSafeguardState = this.scalingSafeguardState;
    // If we've already exhausted all scaling steps, do nothing
    if (
      // eslint-disable-next-line no-restricted-properties
      width < Object.values(estimatedScalingWidths).pop() ||
      !currentSafeguardState
    ) {
      return false;
    }

    this.props.onInfo?.(
      `Scaling overflow detected. Scaling safeguard state: ${currentSafeguardState}`
    );

    if (
      currentSafeguardState.type === CoreScalingState &&
      !this.deterministicRibbonScalingEnabled
    ) {
      // Ribbon content has overflowed the container but there are more scaling steps available.
      // Throw out all build-time and cached data to instead measure everything fresh
      this.scalingSafeguardState = {
        type: MeasureFallbackState,
        renderCount: currentSafeguardState.renderCount + 1,
      };
      setEstimatedScalingWidths({});
      knownControlWidths = undefined;
      this.forceResponsiveScalingOff = true;
      this.forceUpdate();
      return true;
    } else if (
      (this.deterministicRibbonScalingEnabled ||
        currentSafeguardState.type === MeasureFallbackState ||
        currentSafeguardState.type === ApplyAdditionalStepsState) &&
      currentSafeguardState.renderCount <= 5 // ensure we don't force infinite renders
    ) {
      // Even after measuring everything on the client, we still weren't able to scale correctly.
      // Adjust our estimates based on the calculated offset
      this.scalingSafeguardState = {
        type: ApplyAdditionalStepsState,
        renderCount: currentSafeguardState.renderCount + 1,
      };
      for (const scalingStepIndex in estimatedScalingWidths) {
        estimatedScalingWidths[scalingStepIndex] += offset;
      }
      this.forceUpdate();
      return true;
    }
    return false;
  }
}

function getRibbonOverflowMenuOpenAndCloseCommandHandler(
  controlGroups: RibbonControlGroupDefinitionSetSLR
): {
  onOverflowMenuOpen?: (parameter: MenuOpenOnExecuteParameter) => void;
  onOverflowMenuClose?: (id: string) => void;
} {
  const [controlGroupWithHandler] = controlGroups.groups.filter(
    (controlGroup) => !controlGroup.hasOwnOverflow
  );
  if (controlGroupWithHandler) {
    return {
      onOverflowMenuOpen: controlGroupWithHandler.onOverflowMenuOpen,
      onOverflowMenuClose: controlGroupWithHandler.onOverflowMenuClose,
    };
  }
  return {};
}
